import React, { useState, useRef, useEffect } from 'react'
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import Layout from '../components/Layout'

const NAV = [
  { path: '/',           icon: '🏠', label: 'Home' },
  { path: '/ai',         icon: '🤖', label: 'AI Concierge' },
  { path: '/content',    icon: '📅', label: 'Content' },
  { path: '/deals',      icon: '💰', label: 'Brand Deals' },
  { path: '/nil',        icon: '🎓', label: 'NIL' },
  { path: '/overseas',   icon: '🌍', label: 'Overseas' },
  { path: '/agents',     icon: '🤝', label: 'Agent Resources' },
  { path: '/finance',    icon: '💵', label: 'Financial' },
  { path: '/contacts',   icon: '📇', label: 'Contacts' },
  { path: '/vendors',    icon: '📋', label: 'Vendors' },
  { path: '/documents',  icon: '📁', label: 'Documents' },
  { path: '/report',     icon: '📊', label: 'Monthly Report' },
]

function usePageTitle() {
  const { pathname } = useLocation()
  const map = {
    '/': 'Dashboard', '/ai': 'AI Concierge', '/content': 'Content Calendar',
    '/deals': 'Brand Deals', '/nil': 'NIL Opportunities', '/overseas': 'Overseas Opportunities',
    '/agents': 'Agent Resources', '/finance': 'Financial Resources',
    '/contacts': 'Contact Database', '/vendors': 'Vendor Network',
    '/documents': 'Documents', '/report': 'Monthly Report'
  }
  return map[pathname] || 'Dashboard'
}

export default function AthleteDashboard({ profile }) {
  const title = usePageTitle()
  return (
    <Layout profile={profile} navItems={NAV} title={title}>
      <Routes>
        <Route path="/"          element={<Home profile={profile} />} />
        <Route path="/ai"        element={<AIChat profile={profile} />} />
        <Route path="/content"   element={<ContentCalendar />} />
        <Route path="/deals"     element={<BrandDeals />} />
        <Route path="/nil"       element={<NILOpportunities />} />
        <Route path="/overseas"  element={<OverseasOpportunities />} />
        <Route path="/agents"    element={<AgentResources />} />
        <Route path="/finance"   element={<FinancialResources />} />
        <Route path="/contacts"  element={<ContactDatabase />} />
        <Route path="/vendors"   element={<VendorNetwork />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/report"    element={<MonthlyReport profile={profile} />} />
      </Routes>
    </Layout>
  )
}

/* ── SHARED COMPONENTS ──────────────────────────────────────────────────── */
function SectionLabel({ children }) {
  return (
    <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 11, fontWeight: 700,
      letterSpacing: 4, textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 16,
      display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ width: 24, height: 2, background: 'var(--gold)', display: 'inline-block' }} />
      {children}
    </div>
  )
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div className="fade-up" style={{ background: 'var(--card)', border: '1px solid var(--border)',
      borderTop: `3px solid ${accent || 'var(--gold)'}`, padding: '20px 22px' }}>
      <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 10, fontWeight: 700,
        letterSpacing: 3, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8 }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 38, color: accent || 'var(--gold)',
        lineHeight: 1, marginBottom: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--muted)' }}>{sub}</div>}
    </div>
  )
}

function Badge({ children, color }) {
  const c = color || 'var(--gold)'
  return (
    <span style={{ fontFamily: 'var(--font-condensed)', fontSize: 10, fontWeight: 700,
      letterSpacing: 2, textTransform: 'uppercase', padding: '3px 10px',
      background: c + '22', color: c, border: `1px solid ${c}44`, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  )
}

function Card({ children, style }) {
  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--border)',
      padding: '20px 22px', ...style }}>
      {children}
    </div>
  )
}

function ResourceRow({ icon, title, desc, tag, tagColor, action, actionLabel }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, padding: '16px 20px',
      borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
      <span style={{ fontSize: 24, flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 180 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--white)', marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>{desc}</div>
        {tag && <Badge color={tagColor}>{tag}</Badge>}
      </div>
      {action && (
        <a href={action} target="_blank" rel="noreferrer"
          style={{ background: 'var(--gold)', color: 'var(--black)', border: 'none',
            fontFamily: 'var(--font-condensed)', fontSize: 11, fontWeight: 800, letterSpacing: 2,
            textTransform: 'uppercase', padding: '8px 16px', cursor: 'pointer', textDecoration: 'none',
            flexShrink: 0, display: 'inline-block' }}>
          {actionLabel || 'Open →'}
        </a>
      )}
    </div>
  )
}

/* ── HOME ─────────────────────────────────────────────────────────────────── */
function Home({ profile }) {
  const navigate = useNavigate()
  const name = profile?.full_name?.split(' ')[0] || 'Athlete'
  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : 'Good Evening'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div className="fade-up" style={{ background: 'var(--gold)', padding: '28px 32px',
        position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', right: -10, top: '50%', transform: 'translateY(-50%)',
          fontFamily: 'var(--font-display)', fontSize: 160, color: 'rgba(0,0,0,0.06)',
          lineHeight: 1, pointerEvents: 'none' }}>TPO</div>
        <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 11, fontWeight: 700,
          letterSpacing: 4, textTransform: 'uppercase', color: 'rgba(0,0,0,0.45)', marginBottom: 6 }}>{greeting}</div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 44, color: 'var(--black)',
          lineHeight: 0.95, marginBottom: 8 }}>{name}.</div>
        <div style={{ fontSize: 14, color: 'rgba(0,0,0,0.55)' }}>Your off-court business is running. Here's everything in one place.</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12 }}>
        <StatCard label="Package" value={profile?.package_tier || 'Build'} sub="Current plan" accent="var(--gold)" />
        <StatCard label="Active Deals" value="3" sub="In pipeline" accent="var(--green)" />
        <StatCard label="Posts This Month" value="24" sub="Scheduled" accent="#1565C0" />
        <StatCard label="Revenue" value="$2.4K" sub="From brand deals" accent="#9C27B0" />
      </div>

      <SectionLabel>Quick Access</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 10 }}>
        {NAV.slice(1).map(item => (
          <button key={item.path} onClick={() => navigate(item.path)}
            style={{ background: 'var(--card)', border: '1px solid var(--border)', padding: '16px 14px',
              display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8,
              cursor: 'pointer', transition: 'all 0.2s', textAlign: 'left' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--gold)'; e.currentTarget.style.background = 'var(--card2)' }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--card)' }}>
            <span style={{ fontSize: 22 }}>{item.icon}</span>
            <span style={{ fontFamily: 'var(--font-condensed)', fontSize: 11, fontWeight: 700,
              letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--white)' }}>{item.label}</span>
          </button>
        ))}
      </div>

      <SectionLabel>Recent Activity</SectionLabel>
      <Card>
        {[
          { icon: '✅', text: 'Media kit updated with new Instagram stats', time: '2 hours ago' },
          { icon: '📧', text: 'Sponsorship outreach sent to Nike Regional', time: '1 day ago' },
          { icon: '📅', text: '8 posts scheduled for next week', time: '2 days ago' },
          { icon: '📁', text: 'LLC documents uploaded', time: '3 days ago' },
          { icon: '💰', text: 'New opportunity added: Podcast appearance', time: '4 days ago' },
        ].map((item, i, arr) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 0',
            borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>{item.icon}</span>
            <span style={{ flex: 1, fontSize: 14, color: 'var(--light)' }}>{item.text}</span>
            <span style={{ fontFamily: 'var(--font-condensed)', fontSize: 11, color: 'var(--muted)', whiteSpace: 'nowrap' }}>{item.time}</span>
          </div>
        ))}
      </Card>
    </div>
  )
}

/* ── AI CONCIERGE ─────────────────────────────────────────────────────────── */
function AIChat({ profile }) {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: `What's good ${profile?.full_name?.split(' ')[0] || 'Athlete'} 👋 I'm your Player Office AI — ask me anything about your brand, business, deals, or strategy. I'm here to help you build.` }
  ])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const bottomRef = useRef(null)

  const suggestions = [
    'How do I respond to a brand deal inquiry?',
    'Write my bio for a podcast appearance',
    'What should I charge for an Instagram post?',
    'How do I set up a business bank account?',
    'Draft a sponsorship pitch email',
    'What tax write-offs do athletes get?',
  ]

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])

  async function send(text) {
    if (!text.trim() || loading) return
    const userMsg = { role: 'user', content: text }
    setMessages(prev => [...prev, userMsg])
    setInput('')
    setLoading(true)
    const history = [...messages, userMsg].map(m => ({ role: m.role, content: m.content }))
    const systemPrompt = `You are the AI concierge for The Player Office, a premium off-court business operations platform for professional athletes. You are speaking with ${profile?.full_name || 'an athlete'} on the ${profile?.package_tier || 'Build'} package. Help with brand strategy, business decisions, content ideas, sponsorship outreach, contract questions, financial basics, and off-court business building. Tone: warm, direct, like a former pro who also understands business. Keep responses focused and actionable — 2-4 paragraphs max unless asked for detail. Always end with a clear next step.`
    try {
      const res = await fetch('/proxy.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ model: 'claude-sonnet-4-20250514', max_tokens: 1000, system: systemPrompt, messages: history })
      })
      const data = await res.json()
      const reply = data.content?.[0]?.text || "Connection issue — try again in a moment."
      setMessages(prev => [...prev, { role: 'assistant', content: reply }])
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "Connection issue — try again in a moment." }])
    }
    setLoading(false)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', maxWidth: 800 }}>
      <SectionLabel>Your Personal Business AI — Available 24/7</SectionLabel>
      <div style={{ flex: 1, overflowY: 'auto', background: 'var(--card)', border: '1px solid var(--border)',
        borderBottom: 'none', padding: '16px', display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 0 }}>
        {messages.map((msg, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{ maxWidth: '82%', padding: '12px 15px', fontSize: 14, lineHeight: 1.7,
              background: msg.role === 'user' ? 'var(--gold)' : 'var(--dark)',
              color: msg.role === 'user' ? 'var(--black)' : 'var(--light)',
              border: msg.role === 'user' ? 'none' : '1px solid var(--border)',
              fontWeight: msg.role === 'user' ? 500 : 400, whiteSpace: 'pre-wrap' }}>
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ background: 'var(--dark)', border: '1px solid var(--border)',
            padding: '12px 15px', color: 'var(--muted)', fontSize: 13, fontStyle: 'italic' }} className="pulse">
            Thinking...
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      <div style={{ background: 'var(--dark)', border: '1px solid var(--border)', borderTop: 'none',
        borderBottom: 'none', padding: '10px 12px', display: 'flex', flexWrap: 'wrap', gap: 7 }}>
        {suggestions.slice(0, 4).map((s, i) => (
          <button key={i} onClick={() => send(s)}
            style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--gold)',
              fontFamily: 'var(--font-condensed)', fontSize: 11, fontWeight: 600, letterSpacing: 1,
              padding: '6px 12px', cursor: 'pointer', transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(212,169,64,0.1)'; e.currentTarget.style.borderColor = 'var(--gold)' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'var(--border)' }}>
            {s}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', border: '1px solid var(--border)', background: 'var(--dark)' }}>
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send(input)}
          placeholder="Ask anything about your brand, deals, or business..."
          style={{ flex: 1, padding: '13px 15px', background: 'transparent', border: 'none',
            fontSize: 14, color: 'var(--white)', outline: 'none' }} />
        <button onClick={() => send(input)} disabled={loading || !input.trim()}
          style={{ background: input.trim() && !loading ? 'var(--gold)' : 'var(--border)',
            color: 'var(--black)', border: 'none', padding: '13px 22px',
            fontFamily: 'var(--font-condensed)', fontSize: 12, fontWeight: 800,
            letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer', transition: 'background 0.2s' }}>
          Send
        </button>
      </div>
    </div>
  )
}

/* ── CONTENT CALENDAR ─────────────────────────────────────────────────────── */
function ContentCalendar() {
  const [posts, setPosts] = useState([
    { id: 1, date: '2026-05-28', platform: 'Instagram', caption: 'The grind never stops. Another day another session. 🏀 #ProLife', status: 'Pending Approval', type: 'Photo' },
    { id: 2, date: '2026-05-29', platform: 'TikTok', caption: 'Behind the scenes of my morning routine — 5am hits different when you got goals', status: 'Approved', type: 'Video' },
    { id: 3, date: '2026-05-30', platform: 'Twitter/X', caption: 'Grateful for the journey. Every setback is a setup. Keep building.', status: 'Approved', type: 'Text' },
    { id: 4, date: '2026-06-01', platform: 'Instagram', caption: 'New week new energy. What are you building this week?', status: 'Pending Approval', type: 'Reel' },
    { id: 5, date: '2026-06-05', platform: 'TikTok', caption: "Camp registration is open — link in bio. Let's build the next generation 🏀", status: 'Pending Approval', type: 'Video' },
  ])

  const statusColor = { 'Pending Approval': 'var(--gold)', 'Approved': 'var(--green)', 'Scheduled': '#1565C0' }
  const platformColor = { 'Instagram': '#E1306C', 'TikTok': '#ff0050', 'Twitter/X': '#1DA1F2' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12 }}>
        <StatCard label="Total Posts" value={posts.length} accent="var(--gold)" />
        <StatCard label="Approved" value={posts.filter(p => p.status === 'Approved').length} accent="var(--green)" />
        <StatCard label="Pending" value={posts.filter(p => p.status === 'Pending Approval').length} accent="var(--gold)" />
      </div>
      <SectionLabel>Scheduled Posts</SectionLabel>
      <Card style={{ padding: 0 }}>
        {posts.map((post, i) => (
          <div key={post.id} style={{ padding: '16px 18px', borderBottom: i < posts.length - 1 ? '1px solid var(--border)' : 'none',
            display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ background: 'var(--dark)', padding: '6px 10px', textAlign: 'center', minWidth: 48, flexShrink: 0 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, color: 'var(--gold)', lineHeight: 1 }}>{new Date(post.date).getDate()}</div>
              <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 9, letterSpacing: 2, color: 'var(--muted)', textTransform: 'uppercase' }}>{new Date(post.date).toLocaleString('default', { month: 'short' })}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 7, flexWrap: 'wrap', alignItems: 'center' }}>
                <Badge color={platformColor[post.platform]}>{post.platform}</Badge>
                <Badge color={statusColor[post.status]}>{post.status}</Badge>
              </div>
              <div style={{ fontSize: 13, color: 'var(--light)', lineHeight: 1.5 }}>{post.caption}</div>
            </div>
            {post.status === 'Pending Approval' && (
              <button onClick={() => setPosts(prev => prev.map(p => p.id === post.id ? { ...p, status: 'Approved' } : p))}
                style={{ background: 'var(--green)', border: 'none', color: 'var(--white)', fontFamily: 'var(--font-condensed)',
                  fontSize: 10, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', padding: '7px 14px', cursor: 'pointer' }}>
                ✓ Approve
              </button>
            )}
          </div>
        ))}
      </Card>
    </div>
  )
}

/* ── BRAND DEALS ──────────────────────────────────────────────────────────── */
function BrandDeals() {
  const deals = [
    { brand: 'SportsTech Co', type: 'Instagram + TikTok Campaign', value: '$2,500', status: 'Negotiating', notes: 'They want 3 posts + 2 stories. Waiting on contract.' },
    { brand: 'Local Gym Chain', type: 'Brand Ambassador', value: '$800/mo', status: 'Outreach Sent', notes: 'Sent pitch email. Following up Friday.' },
    { brand: 'Podcast Appearance', type: 'Sports Business Podcast', value: '$500', status: 'Confirmed', notes: 'Recording June 10th.' },
    { brand: 'Apparel Brand', type: 'Merch Collab', value: '$3,000', status: 'In Review', notes: "Sent media kit. They're reviewing our proposal." },
  ]
  const statusColor = { 'Confirmed': 'var(--green)', 'Negotiating': 'var(--gold)', 'Outreach Sent': '#1565C0', 'In Review': '#9C27B0' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12 }}>
        <StatCard label="Active Deals" value={deals.length} accent="var(--gold)" />
        <StatCard label="Pipeline Value" value="$6.8K" accent="var(--green)" />
        <StatCard label="Confirmed" value={deals.filter(d => d.status === 'Confirmed').length} accent="var(--green)" />
      </div>
      <SectionLabel>Active Pipeline</SectionLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {deals.map((deal, i) => (
          <Card key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
              <div>
                <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 17, fontWeight: 800,
                  letterSpacing: 1, color: 'var(--white)', textTransform: 'uppercase', marginBottom: 3 }}>{deal.brand}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>{deal.type}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 26, color: 'var(--gold)', lineHeight: 1 }}>{deal.value}</div>
                <Badge color={statusColor[deal.status]}>{deal.status}</Badge>
              </div>
            </div>
            <div style={{ fontSize: 13, color: 'var(--light)', background: 'var(--dark)', padding: '9px 13px', borderLeft: '3px solid var(--border)' }}>{deal.notes}</div>
          </Card>
        ))}
      </div>
    </div>
  )
}

/* ── NIL OPPORTUNITIES ────────────────────────────────────────────────────── */
function NILOpportunities() {
  const opportunities = [
    { brand: 'Under Armour Collegiate', category: 'Apparel', value: '$500–$2,000', deadline: 'Rolling', status: 'Open', desc: 'Seeking collegiate athletes for social media campaigns. Must have 5K+ followers.' },
    { brand: 'Gatorade Campus', category: 'Sports Nutrition', value: '$300–$800', deadline: 'Jun 30', status: 'Open', desc: 'Product endorsement and content creation. 3 posts per month required.' },
    { brand: 'Local Car Dealership Network', category: 'Local Business', value: '$200–$500', deadline: 'Rolling', status: 'Open', desc: 'Philadelphia area dealerships seeking local athlete endorsers.' },
    { brand: 'Sports Nutrition Brand', category: 'Supplements', value: '$100–$400', deadline: 'Rolling', status: 'Open', desc: 'Ambassador program. Free product + commission on sales.' },
    { brand: 'Youth Basketball Camp', category: 'Events', value: '$250–$600', deadline: 'Jul 15', status: 'Open', desc: 'Appearance fees for youth camp guest instructors. Summer 2026.' },
  ]

  const resources = [
    { icon: '📋', title: 'NIL Agreement Template', desc: 'Standard NIL deal template reviewed by sports attorneys. Customize for each brand.', tag: 'Template', action: '#' },
    { icon: '📊', title: 'NIL Rate Calculator', desc: 'Calculate what you should charge based on your follower count and engagement rate.', tag: 'Tool', action: '#' },
    { icon: '⚖️', title: 'NCAA NIL Rules Guide', desc: 'Current NCAA NIL rules and state-by-state regulations updated for 2026.', tag: 'Guide', action: 'https://ncaa.org/sports/2021/1/14/nil.aspx' },
    { icon: '💰', title: 'Tax Guide for NIL Athletes', desc: 'Understanding NIL income, quarterly taxes, and deductions for college athletes.', tag: 'Finance', action: '#' },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12 }}>
        <StatCard label="Open Deals" value={opportunities.length} accent="var(--gold)" />
        <StatCard label="Avg Value" value="$600" sub="Per opportunity" accent="var(--green)" />
        <StatCard label="Deadlines" value="2" sub="This month" accent="var(--red)" />
      </div>

      <SectionLabel>Active NIL Opportunities</SectionLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {opportunities.map((opp, i) => (
          <Card key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 10 }}>
              <div>
                <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 15, fontWeight: 800,
                  color: 'var(--white)', textTransform: 'uppercase', marginBottom: 4 }}>{opp.brand}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Badge color="var(--gold)">{opp.category}</Badge>
                  <Badge color="var(--green)">{opp.status}</Badge>
                  {opp.deadline !== 'Rolling' && <Badge color="var(--red)">Deadline: {opp.deadline}</Badge>}
                </div>
              </div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--gold)' }}>{opp.value}</div>
            </div>
            <div style={{ fontSize: 13, color: 'var(--light)' }}>{opp.desc}</div>
          </Card>
        ))}
      </div>

      <SectionLabel>NIL Resources</SectionLabel>
      <Card style={{ padding: 0 }}>
        {resources.map((r, i) => (
          <ResourceRow key={i} {...r} tagColor="var(--gold)" actionLabel="View →" />
        ))}
      </Card>
    </div>
  )
}

/* ── OVERSEAS OPPORTUNITIES ───────────────────────────────────────────────── */
function OverseasOpportunities() {
  const leagues = [
    { country: '🇪🇸 Spain', league: 'ACB / LEB Gold', avgSalary: '$80K–$500K', season: 'Oct–Jun', notes: 'Top European league. Housing included. Strong American market.' },
    { country: '🇫🇷 France', league: 'Pro A / Pro B', avgSalary: '$40K–$300K', season: 'Oct–May', notes: 'Great lifestyle. Paris market. Strong development league.' },
    { country: '🇮🇱 Israel', league: 'BSL', avgSalary: '$50K–$400K', season: 'Oct–May', notes: 'Tax-free income. High standard of living. Maccabi Tel Aviv is top club.' },
    { country: '🇩🇪 Germany', league: 'BBL', avgSalary: '$40K–$200K', season: 'Oct–May', notes: 'Growing league. Strong infrastructure. Central European location.' },
    { country: '🇦🇺 Australia', league: 'NBL', avgSalary: '$50K–$250K', season: 'Oct–Mar', notes: 'English speaking. NBA pipeline league. Strong player support.' },
    { country: '🇹🇷 Turkey', league: 'BSL', avgSalary: '$60K–$600K', season: 'Oct–May', notes: 'Highest average salaries in Europe. Istanbul market.' },
    { country: '🇮🇹 Italy', league: 'Lega A', avgSalary: '$40K–$300K', season: 'Oct–Jun', notes: 'Great lifestyle. Historic basketball culture. Multiple Americans per team.' },
    { country: '🇬🇷 Greece', league: 'A1', avgSalary: '$30K–$200K', season: 'Oct–May', notes: 'Mediterranean lifestyle. Panathinaikos and Olympiacos are top clubs.' },
  ]

  const resources = [
    { icon: '📝', title: 'Overseas Contract Checklist', desc: 'What to look for in every overseas contract — housing, flights, insurance, buyout clauses.', tag: 'Essential', action: '#' },
    { icon: '🏦', title: 'International Banking Guide', desc: 'How to maintain US bank accounts while receiving foreign currency. Wire transfer guide.', tag: 'Finance', action: '#' },
    { icon: '🛂', title: 'Visa & Work Permit Guide', desc: 'Work permit requirements by country. How to expedite the process.', tag: 'Legal', action: '#' },
    { icon: '💰', title: 'Overseas Tax Guide for Americans', desc: 'FBAR requirements, foreign earned income exclusion, and how to avoid double taxation.', tag: 'Tax', action: '#' },
    { icon: '📱', title: 'International SIM & Banking Setup', desc: 'Best SIM cards by country, how to send money home, apps that work internationally.', tag: 'Practical', action: '#' },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Card style={{ background: 'rgba(212,169,64,0.08)', borderColor: 'var(--gold)' }}>
        <div style={{ fontSize: 14, color: 'var(--light)', lineHeight: 1.7 }}>
          <strong style={{ color: 'var(--gold)' }}>Founder's Note:</strong> I played professionally in Australia, Austria, Portugal, and Iran. Everything in this section comes from personal experience. If you have questions about a specific country or league — ask the AI Concierge.
        </div>
      </Card>

      <SectionLabel>League Overview by Country</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 12 }}>
        {leagues.map((l, i) => (
          <Card key={i}>
            <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 16, fontWeight: 800,
              color: 'var(--white)', marginBottom: 4 }}>{l.country}</div>
            <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 11, fontWeight: 700,
              letterSpacing: 2, color: 'var(--gold)', textTransform: 'uppercase', marginBottom: 8 }}>{l.league}</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
              <Badge color="var(--green)">{l.avgSalary}</Badge>
              <Badge color="#1565C0">{l.season}</Badge>
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.6 }}>{l.notes}</div>
          </Card>
        ))}
      </div>

      <SectionLabel>Overseas Resources</SectionLabel>
      <Card style={{ padding: 0 }}>
        {resources.map((r, i) => (
          <ResourceRow key={i} {...r} tagColor="var(--gold)" actionLabel="View →" />
        ))}
      </Card>
    </div>
  )
}

/* ── AGENT RESOURCES ──────────────────────────────────────────────────────── */
function AgentResources() {
  const agentGuides = [
    { icon: '🔍', title: 'How to Evaluate a Sports Agent', desc: 'Red flags, contract terms, NBPA certification requirements, and how to negotiate your representation deal.', tag: 'Guide' },
    { icon: '📜', title: 'Agent Agreement Breakdown', desc: 'Standard agent contract terms explained. What to negotiate, what to accept, what to reject.', tag: 'Legal' },
    { icon: '💬', title: 'Questions to Ask Every Agent', desc: '20 questions every athlete should ask before signing with an agent. Based on real player experiences.', tag: 'Checklist' },
    { icon: '⚠️', title: 'Common Agent Scams & Red Flags', desc: 'How to spot predatory agents, upfront fee scams, and agents who lack real industry relationships.', tag: 'Warning' },
    { icon: '🤝', title: 'Working With Your Agent', desc: 'How to have a productive agent relationship. Communication, expectations, and how to get more out of your representation.', tag: 'Strategy' },
    { icon: '🔄', title: 'How to Switch Agents', desc: 'Step-by-step guide to terminating an agent relationship and finding new representation professionally.', tag: 'Process' },
  ]

  const associations = [
    { name: 'NBPA (NBA Players Association)', desc: 'Resources, programs, and support for NBA/G-League players.', url: 'https://nbpa.com', tag: 'NBA' },
    { name: 'WNBPA (WNBA Players Association)', desc: 'Player resources, benefits, and advocacy for WNBA athletes.', url: 'https://wnbpa.com', tag: 'WNBA' },
    { name: 'NFLPA (NFL Players Association)', desc: 'Financial education, career development, and player services.', url: 'https://nflpa.com', tag: 'NFL' },
    { name: 'NBPA Foundation', desc: 'Programs for current and former NBA players. Business development and transition support.', url: 'https://nbpafoundation.org', tag: 'Foundation' },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <SectionLabel>Agent Education Resources</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 12 }}>
        {agentGuides.map((g, i) => (
          <Card key={i}>
            <div style={{ fontSize: 24, marginBottom: 10 }}>{g.icon}</div>
            <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 14, fontWeight: 800,
              color: 'var(--white)', textTransform: 'uppercase', marginBottom: 6 }}>{g.title}</div>
            <Badge color="var(--gold)">{g.tag}</Badge>
            <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginTop: 8 }}>{g.desc}</div>
          </Card>
        ))}
      </div>

      <SectionLabel>Player Associations</SectionLabel>
      <Card style={{ padding: 0 }}>
        {associations.map((a, i) => (
          <ResourceRow key={i} icon="🏛️" title={a.name} desc={a.desc}
            tag={a.tag} tagColor="var(--gold)" action={a.url} actionLabel="Visit →" />
        ))}
      </Card>
    </div>
  )
}

/* ── FINANCIAL RESOURCES ──────────────────────────────────────────────────── */
function FinancialResources() {
  const tools = [
    { icon: '🏦', title: 'Business Bank Account Setup', desc: 'Step-by-step guide to opening a business checking account using your LLC and EIN. Recommended: Chase Business Complete or Relay.', tag: 'Banking' },
    { icon: '📊', title: 'Athlete Budget Template', desc: 'Monthly budget template built for professional athletes — covers contract income, off-court income, taxes, savings, and investments.', tag: 'Budgeting' },
    { icon: '🧾', title: 'Tax Guide for Pro Athletes', desc: "Quarterly estimated taxes, state tax obligations for road games, business deductions, and working with a sports CPA.", tag: 'Taxes' },
    { icon: '💼', title: 'LLC & EIN Setup Guide', desc: 'Why every athlete needs an LLC, how to set one up in your state, and how to get your EIN from the IRS for free.', tag: 'Business' },
    { icon: '📈', title: 'Investing Basics for Athletes', desc: 'Index funds, real estate, and alternative investments explained for athletes. What to do with your first $50,000.', tag: 'Investing' },
    { icon: '🛡️', title: 'Insurance Guide', desc: 'Health insurance during offseason, disability insurance, business liability coverage, and what your contract may not cover.', tag: 'Insurance' },
    { icon: '💳', title: 'Credit Building Guide', desc: 'How to build and maintain excellent credit as a professional athlete. Business credit vs personal credit.', tag: 'Credit' },
    { icon: '🏠', title: 'Real Estate for Athletes', desc: "Buying vs renting during your career, relocation strategies, and how to build real estate wealth on an athlete's timeline.", tag: 'Real Estate' },
  ]

  const checklist = [
    { done: true, item: 'Open LLC' },
    { done: true, item: 'Get EIN from IRS' },
    { done: false, item: 'Open business bank account' },
    { done: false, item: 'Set up quarterly tax payments' },
    { done: false, item: 'Get disability insurance' },
    { done: false, item: 'Start retirement account (Roth IRA)' },
    { done: false, item: 'Build 3-month emergency fund' },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <SectionLabel>Financial Checklist</SectionLabel>
          <Card>
            {checklist.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
                borderBottom: i < checklist.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ width: 20, height: 20, borderRadius: 2, flexShrink: 0,
                  background: item.done ? 'var(--green)' : 'transparent',
                  border: `2px solid ${item.done ? 'var(--green)' : 'var(--border)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontSize: 11, fontWeight: 700 }}>
                  {item.done ? '✓' : ''}
                </div>
                <span style={{ fontSize: 13, color: item.done ? 'var(--muted)' : 'var(--white)',
                  textDecoration: item.done ? 'line-through' : 'none' }}>{item.item}</span>
              </div>
            ))}
          </Card>
        </div>
        <div>
          <SectionLabel>Quick Stats</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <StatCard label="Foundation" value="65%" sub="Financial setup complete" accent="var(--green)" />
            <StatCard label="Next Step" value="Bank" sub="Open business account" accent="var(--gold)" />
          </div>
        </div>
      </div>

      <SectionLabel>Financial Resource Library</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 12 }}>
        {tools.map((t, i) => (
          <Card key={i}>
            <div style={{ fontSize: 22, marginBottom: 8 }}>{t.icon}</div>
            <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 13, fontWeight: 800,
              color: 'var(--white)', textTransform: 'uppercase', marginBottom: 6 }}>{t.title}</div>
            <Badge color="var(--gold)">{t.tag}</Badge>
            <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.6, marginTop: 8 }}>{t.desc}</div>
          </Card>
        ))}
      </div>
    </div>
  )
}

/* ── CONTACT DATABASE ─────────────────────────────────────────────────────── */
function ContactDatabase() {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('All')

  const contacts = [
    { name: 'Marcus Johnson', role: 'Photographer', category: 'Creative', city: 'Philadelphia, PA', phone: '(215) 555-0101', ig: '@marcusphotos', rating: 5, notes: 'Sports + lifestyle. Book 2 weeks out.' },
    { name: 'Chef Antoine', role: 'Private Chef', category: 'Lifestyle', city: 'Philadelphia, PA', phone: '(215) 555-0102', ig: '@chefantoine', rating: 5, notes: 'Meal prep + event catering.' },
    { name: 'Dr. Williams CPA', role: 'Accountant / CPA', category: 'Finance', city: 'Philadelphia, PA', phone: '(215) 555-0103', ig: '', rating: 5, notes: 'Athlete tax specialist.' },
    { name: 'Elite PT Studio', role: 'Physical Therapy', category: 'Health', city: 'Philadelphia, PA', phone: '(215) 555-0104', ig: '@elitept', rating: 4, notes: 'Sports rehab. Same-week appts.' },
    { name: 'Street Legal Transport', role: 'Private Driver', category: 'Lifestyle', city: 'Philadelphia, PA', phone: '(267) 555-0105', ig: '', rating: 5, notes: 'Airport, appearances, daily. 24/7.' },
    { name: 'King Cuts Barber', role: 'Barber', category: 'Lifestyle', city: 'Philadelphia, PA', phone: '(215) 555-0106', ig: '@kingcuts', rating: 5, notes: 'Home visits available.' },
    { name: 'Smith Law Group', role: 'Sports Attorney', category: 'Legal', city: 'Philadelphia, PA', phone: '(215) 555-0107', ig: '', rating: 5, notes: 'Contract review and NIL deals.' },
    { name: 'FitBody Nutrition', role: 'Sports Nutritionist', category: 'Health', city: 'Philadelphia, PA', phone: '(215) 555-0108', ig: '@fitbodynutrition', rating: 4, notes: 'Performance nutrition plans.' },
  ]

  const categories = ['All', 'Creative', 'Finance', 'Health', 'Legal', 'Lifestyle']
  const catColor = { 'Creative': '#9C27B0', 'Finance': 'var(--green)', 'Health': '#E91E63', 'Legal': '#1565C0', 'Lifestyle': 'var(--gold)' }

  const filtered = contacts.filter(c => {
    const matchSearch = c.name.toLowerCase().includes(search.toLowerCase()) || c.role.toLowerCase().includes(search.toLowerCase())
    const matchFilter = filter === 'All' || c.category === filter
    return matchSearch && matchFilter
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <SectionLabel>Your Vetted Contact Network</SectionLabel>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search contacts..."
          style={{ flex: 1, minWidth: 200, padding: '10px 14px', background: 'var(--card)',
            border: '1px solid var(--border)', color: 'var(--white)', fontSize: 14 }} />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {categories.map(cat => (
            <button key={cat} onClick={() => setFilter(cat)}
              style={{ background: filter === cat ? 'var(--gold)' : 'var(--card)',
                color: filter === cat ? 'var(--black)' : 'var(--light)',
                border: '1px solid var(--border)', fontFamily: 'var(--font-condensed)',
                fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase',
                padding: '8px 14px', cursor: 'pointer', transition: 'all 0.15s' }}>
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(270px,1fr))', gap: 12 }}>
        {filtered.map((c, i) => (
          <Card key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <div>
                <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 14, fontWeight: 800,
                  color: 'var(--white)', textTransform: 'uppercase', marginBottom: 3 }}>{c.name}</div>
                <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 11, fontWeight: 700,
                  letterSpacing: 2, color: 'var(--gold)', textTransform: 'uppercase' }}>{c.role}</div>
              </div>
              <Badge color={catColor[c.category]}>{c.category}</Badge>
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10, fontStyle: 'italic' }}>{c.notes}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <a href={`tel:${c.phone}`}
                style={{ flex: 1, background: 'var(--gold)', color: 'var(--black)', border: 'none',
                  fontFamily: 'var(--font-condensed)', fontSize: 10, fontWeight: 800, letterSpacing: 2,
                  textTransform: 'uppercase', padding: '7px', textAlign: 'center', cursor: 'pointer',
                  textDecoration: 'none', display: 'block' }}>📞 Call</a>
              {c.ig && (
                <a href={`https://instagram.com/${c.ig.replace('@', '')}`} target="_blank" rel="noreferrer"
                  style={{ flex: 1, background: 'var(--dark)', border: '1px solid var(--border)',
                    color: 'var(--light)', fontFamily: 'var(--font-condensed)', fontSize: 10, fontWeight: 800,
                    letterSpacing: 2, textTransform: 'uppercase', padding: '7px', textAlign: 'center',
                    textDecoration: 'none', display: 'block' }}>Instagram</a>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  )
}

/* ── VENDOR NETWORK ───────────────────────────────────────────────────────── */
function VendorNetwork() {
  const vendors = [
    { name: 'Marcus Johnson', role: 'Photographer', city: 'Philadelphia, PA', phone: '(215) 555-0101', ig: '@marcusphotos', rating: 5, notes: 'Sports + lifestyle. Book 2 weeks out.' },
    { name: 'Chef Antoine', role: 'Private Chef', city: 'Philadelphia, PA', phone: '(215) 555-0102', ig: '@chefantoine', rating: 5, notes: 'Meal prep + event catering. Weekly packages.' },
    { name: 'Dr. Williams CPA', role: 'Accountant / CPA', city: 'Philadelphia, PA', phone: '(215) 555-0103', ig: '', rating: 5, notes: 'Athlete tax specialist. Handles overseas income.' },
    { name: 'Elite PT Studio', role: 'Physical Therapy', city: 'Philadelphia, PA', phone: '(215) 555-0104', ig: '@elitept', rating: 4, notes: 'Sports rehab. Same-week appointments.' },
    { name: 'Street Legal Transport', role: 'Private Driver', city: 'Philadelphia, PA', phone: '(267) 555-0105', ig: '', rating: 5, notes: 'Airport, appearances, daily transport. 24/7.' },
    { name: 'King Cuts Barber', role: 'Barber', city: 'Philadelphia, PA', phone: '(215) 555-0106', ig: '@kingcuts', rating: 5, notes: 'Home visits available. Book same day.' },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <SectionLabel>Your Vetted Vendors</SectionLabel>
      <Card style={{ borderColor: 'var(--gold)', background: 'rgba(212,169,64,0.06)' }}>
        <div style={{ fontSize: 13, color: 'var(--light)' }}>Every vendor below is vetted by The Player Office. Trusted connections — not random referrals.</div>
      </Card>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 12 }}>
        {vendors.map((v, i) => (
          <Card key={i}>
            <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 14, fontWeight: 800,
              color: 'var(--white)', textTransform: 'uppercase', marginBottom: 2 }}>{v.name}</div>
            <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 11, fontWeight: 700,
              letterSpacing: 2, color: 'var(--gold)', textTransform: 'uppercase', marginBottom: 8 }}>{v.role}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10, fontStyle: 'italic' }}>{v.notes}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <a href={`tel:${v.phone}`}
                style={{ flex: 1, background: 'var(--gold)', color: 'var(--black)', fontFamily: 'var(--font-condensed)',
                  fontSize: 10, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', padding: '8px',
                  textAlign: 'center', textDecoration: 'none', display: 'block' }}>📞 Call</a>
              {v.ig && (
                <a href={`https://instagram.com/${v.ig.replace('@','')}`} target="_blank" rel="noreferrer"
                  style={{ flex: 1, background: 'var(--dark)', border: '1px solid var(--border)', color: 'var(--light)',
                    fontFamily: 'var(--font-condensed)', fontSize: 10, fontWeight: 800, letterSpacing: 2,
                    textTransform: 'uppercase', padding: '8px', textAlign: 'center', textDecoration: 'none', display: 'block' }}>IG</a>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  )
}

/* ── DOCUMENTS ────────────────────────────────────────────────────────────── */
function Documents() {
  const docs = [
    { name: 'LLC Certificate', type: 'Legal', date: '2026-04-15', status: 'Uploaded' },
    { name: 'EIN Letter (IRS)', type: 'Legal', date: '2026-04-16', status: 'Uploaded' },
    { name: 'Media Kit — May 2026', type: 'Brand', date: '2026-05-01', status: 'Uploaded' },
    { name: 'Service Agreement — The Player Office', type: 'Contract', date: '2026-04-10', status: 'Signed' },
    { name: 'Brand Deal Contract — SportsTech', type: 'Contract', date: '2026-05-20', status: 'Pending Signature' },
    { name: 'W-9 Tax Form', type: 'Tax', date: '2026-04-20', status: 'Uploaded' },
  ]
  const typeColor = { 'Legal': '#1565C0', 'Brand': '#9C27B0', 'Contract': 'var(--gold)', 'Tax': 'var(--green)' }
  const statusColor = { 'Uploaded': 'var(--green)', 'Signed': 'var(--green)', 'Pending Signature': 'var(--gold)' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <SectionLabel>Your Documents</SectionLabel>
      <Card style={{ padding: 0 }}>
        {docs.map((doc, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '15px 18px',
            borderBottom: i < docs.length - 1 ? '1px solid var(--border)' : 'none', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 20, flexShrink: 0 }}>📄</span>
            <div style={{ flex: 1, minWidth: 160 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--white)', marginBottom: 3 }}>{doc.name}</div>
              <Badge color={typeColor[doc.type]}>{doc.type}</Badge>
            </div>
            <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 11, color: 'var(--muted)' }}>{doc.date}</div>
            <Badge color={statusColor[doc.status]}>{doc.status}</Badge>
          </div>
        ))}
      </Card>
    </div>
  )
}

/* ── MONTHLY REPORT ───────────────────────────────────────────────────────── */
function MonthlyReport({ profile }) {
  const name = profile?.full_name || 'Athlete'
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 780 }}>
      <div style={{ background: 'var(--gold)', padding: '24px 28px' }}>
        <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 11, fontWeight: 700, letterSpacing: 4,
          textTransform: 'uppercase', color: 'rgba(0,0,0,0.4)', marginBottom: 4 }}>Monthly Report</div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 44, color: 'var(--black)', lineHeight: 0.95, marginBottom: 6 }}>{name}</div>
        <div style={{ fontSize: 13, color: 'rgba(0,0,0,0.5)' }}>The Player Office — May 2026</div>
      </div>
      {[
        { title: '✅ What We Accomplished', items: ['LLC filed and EIN received', 'Personal brand website live', 'Media kit built and ready for brands', '24 posts scheduled for May', 'Sponsorship outreach sent to 12 brands'] },
        { title: '💰 Revenue & Deals', items: ['3 active deals — total pipeline $6,800', '1 confirmed podcast at $500', 'Negotiations with SportsTech ($2,500)', 'Apparel collab under review ($3,000)'] },
        { title: '📈 Growth', items: ['Instagram: +840 followers this month', 'Engagement rate: 4.2% (avg: 1.8%)', 'Top post: workout reel (12K views)', 'TikTok: 3 videos, 28K total views'] },
        { title: '🎯 June Focus', items: ['Close SportsTech deal this week', 'Launch camp registration page', 'Record podcast June 10th', 'Build and launch merch store'] },
      ].map((section, i) => (
        <Card key={i}>
          <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 14, fontWeight: 800,
            letterSpacing: 1, textTransform: 'uppercase', color: 'var(--white)', marginBottom: 14 }}>{section.title}</div>
          {section.items.map((item, j) => (
            <div key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13,
              color: 'var(--light)', lineHeight: 1.6, padding: '4px 0' }}>
              <span style={{ width: 5, height: 5, background: 'var(--gold)', flexShrink: 0, marginTop: 6, transform: 'rotate(45deg)' }} />
              {item}
            </div>
          ))}
        </Card>
      ))}
      <Card style={{ textAlign: 'center' }}>
        <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--muted)' }}>Questions? Reach out anytime.</div>
        <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 13, fontWeight: 700, letterSpacing: 2, color: 'var(--gold)', marginTop: 6 }}>anwarcoleman@gmail.com · (267) 275-2483</div>
      </Card>
    </div>
  )
}
